import { useEffect } from 'react';
import { IBrushDrawConfig, IMarkupDrawConfig, type BrushData } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import { events } from '@/events-bus';
import { useFeatureHelper } from '@/hooks/use-feature-helper';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { markupFree } from '@/types';
import { checkImageEditor } from '@/utils/common';
import { BlockContainer } from '../../components';
import { Funcs, ScopeEvents } from '../../constants';
import { Tracker } from '../../utils';
import { getBrushPresets, getMarkupPresets, type BrushPreset, type MarkupPreset } from './config';
import './style.less';
import { useBrushCollapseModel } from '@/store/collapse-models/brush-collapse-model';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { useRecordModel } from '@/store/models/record-model';

const BRUSH_PRESETS = getBrushPresets();
const MARKUP_PRESETS = getMarkupPresets();

const DrawShortcut: React.FC = () => {
    const { $t } = useLocaleStore();

    const featureHelper = useFeatureHelper();

    const setCurrentType = useCollapseModel((actions) => actions.setCurrentType);
    const getCurrentType = useCollapseModel((actions) => actions.getCurrentType);
    const setActualType = useCollapseModel((actions) => actions.setActualType);
    const setFrom = useBrushCollapseModel((actions) => actions.setFrom);
    const getRecordInfo = useRecordModel((actions) => actions.getRecordInfo);

    const openBrushCollapse = () => {
        events.emit(ScopeEvents.FuncUse, Funcs.OpenDrawPanel);

        const isImageEditor = checkImageEditor();

        if (!isImageEditor) {
            // ATTENTION：只需要在海报编辑内才需要添加打开事件埋点
            Tracker.openBrushMaterialCollapse(getRecordInfo()?.id);
            setCurrentType(CollapseType.BRUSH_MENU);
            return;
        }

        setActualType(CollapseType.BRUSH_MENU);
        setFrom(CollapseType.SHORTCUT_MENU);

        featureHelper.use(CollapseType.BRUSH_MENU);
    };

    const addByPreset = (preset: BrushPreset) => {
        Tracker.addBrushOrVectorLayerByPreset();

        const { to, data, needDraw } = preset;

        if (to === 'vector') {
            events.emit(ScopeEvents.FuncUse, Funcs.AddVectorLayer);
            editor.vectorLayer.addByBrushData(data as BrushData);
        }

        if (to === 'brush') {
            if (needDraw) {
                events.emit(ScopeEvents.FuncUse, Funcs.UseDrawTool);
                editor.markupDraw.close();
                editor.brushDraw.open(data as IBrushDrawConfig);
            } else {
                events.emit(ScopeEvents.FuncUse, Funcs.AddBrushLayer);
                editor.brushLayer.addPresetLine(data as IBrushDrawConfig);
            }
        }
    };

    const startUsingMarkup = (preset: MarkupPreset) => {
        events.emit(ScopeEvents.FuncUse, Funcs.UseDrawTool);

        Tracker.startUsingMarkup(`${getRecordInfo().id || ''}`);

        editor.brushDraw.close();
        // 标注笔限免标记
        if (markupFree) {
            preset.data.__vipFuncType = '';
        }
        editor.markupDraw.close();
        setTimeout(() => {
            editor.markupDraw.open(preset.data as IMarkupDrawConfig);
        }, 0);
    };

    useEffect(() => {
        // brush-collapse 面板上也有监听
        // 如果特殊处理，需要判断是否从当前面板触发的绘制
        // 可以在 open 的时候记录绘制的类型 id

        const trackDrawEnd = () => {
            const currentType = getCurrentType();

            if (currentType === CollapseType.SHORTCUT_MENU) {
                Tracker.drawEnd(`${getRecordInfo().id || ''}`);
            }
        };

        editor.markupDraw.onDrawEnd(() => {
            trackDrawEnd();
        });

        editor.brushDraw.onDrawEnd(() => {
            trackDrawEnd();
        });

        // 注册面板退出关闭画笔
        featureHelper.register(CollapseType.SHORTCUT_MENU, {
            leave() {
                console.log('leave shortcut');
                editor.brushDraw.close();
                editor.markupDraw.close();
            },
            enter() {
                console.log('enter shortcut');
            },
        });
    }, []);

    return (
        <BlockContainer
            className="draw-shortcut"
            title={$t((v) => v.collapse.shortcutCollapse.Brush.title)}
            onFindMore={openBrushCollapse}
        >
            <div className="draw-shortcut__btns">
                {BRUSH_PRESETS.map((item) => {
                    const thumbnail = item.thumbnail;
                    const imageStyle = thumbnail.size
                        ? { width: thumbnail.size.width, height: thumbnail.size.height }
                        : {};

                    return (
                        <div
                            key={item.id}
                            className="shortcut-btn draw-shortcut__btn"
                            onClick={() => addByPreset(item)}
                        >
                            <img draggable={false} src={thumbnail.src} style={imageStyle} alt="" />
                        </div>
                    );
                })}

                {MARKUP_PRESETS.map((item) => {
                    const thumbnail = item.thumbnail;
                    const imageStyle = thumbnail.size
                        ? { width: thumbnail.size.width, height: thumbnail.size.height }
                        : {};

                    return (
                        <div
                            key={item.id}
                            className="shortcut-btn draw-shortcut__btn"
                            onClick={() => startUsingMarkup(item)}
                        >
                            <img draggable={false} src={thumbnail.src} style={imageStyle} alt="" />
                        </div>
                    );
                })}
            </div>
        </BlockContainer>
    );
};

export default DrawShortcut;
